import { useGLTF } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Suspense, useLayoutEffect, useMemo, useRef } from "react";
import {
  ACESFilmicToneMapping,
  Box3,
  type DirectionalLight,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  Vector3,
} from "three";

// DEV-ONLY geometry-inspection route (#149 rev. 2). Renders ONLY the pillow GLB —
// no sofa, no fog, no reveal, no pet texture — as a white matte CLAY surface under
// a camera-raking grazing light. That isolates the one signal we tune the mesh
// against: surface normal quality. Grazing light amplifies any rim/triangulation
// roughness into the exact striations the operator sees in the real viewer, but
// with zero Stage confounds. The `pnpm shot --geom` harness orbits (az,el) and
// reads the PNGs, so the geometry loop is automatic. Guarded on import.meta.env.DEV.

declare global {
  interface Window {
    __shotReady?: boolean;
  }
}

interface GeomSearch {
  mesh: string;
  az: number; // camera azimuth, degrees (0 = +Z front)
  el: number; // camera elevation, degrees above the xz-plane
  dist: number; // camera distance, in mesh-radius units
  keyEl: number; // key-light elevation, degrees (low = grazing/raking)
}

function num(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export const Route = createFileRoute("/geomshot")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>): GeomSearch => ({
    mesh: typeof search.mesh === "string" && search.mesh ? search.mesh : "/pillow-sample.glb",
    az: num(search.az, 35),
    el: num(search.el, 18),
    dist: num(search.dist, 1.7),
    keyEl: num(search.keyEl, 14),
  }),
  component: GeomShot,
});

const DEG = Math.PI / 180;

function orbitPosition(center: Vector3, radius: number, azDeg: number, elDeg: number): Vector3 {
  const az = azDeg * DEG;
  const el = elDeg * DEG;
  return new Vector3(
    center.x + radius * Math.cos(el) * Math.sin(az),
    center.y + radius * Math.sin(el),
    center.z + radius * Math.cos(el) * Math.cos(az),
  );
}

// White clay: a single matte non-metal material so shading reads pure geometry,
// with the GLB's smooth vertex normals. No texture, no weave, no normal map.
const CLAY = new MeshStandardMaterial({ color: "#e9ecf0", roughness: 0.62, metalness: 0.0 });
const CLAY_FLAT = new MeshStandardMaterial({
  color: "#e9ecf0",
  roughness: 0.62,
  metalness: 0.0,
  flatShading: true,
});
// Glossier clay for `?env=1` (studio-IBL-like) mode: a lower roughness makes the
// soft environment reflection sweep across the surface, so any residual normal
// banding (the comb / field staircase) shows the way it does in a glTF viewer's
// IBL — the matte grazing-key clay above can hide it. Paired with a ring of lights.
const CLAY_GLOSS = new MeshStandardMaterial({ color: "#e9ecf0", roughness: 0.32, metalness: 0.0 });

function ClayPillow({ mesh, az, el, dist, keyEl }: GeomSearch) {
  const { scene } = useGLTF(mesh);
  const { camera } = useThree();
  const keyRef = useRef<DirectionalLight>(null);

  const search = typeof window !== "undefined" ? window.location.search : "";
  const recompute = search.includes("recompute=1");
  const flat = search.includes("flat=1");
  const env = search.includes("env=1");
  const viewer = search.includes("viewer=1");
  const { center, radius } = useMemo(() => {
    scene.traverse((o: Object3D) => {
      if (o instanceof Mesh) {
        o.material = flat ? CLAY_FLAT : env || viewer ? CLAY_GLOSS : CLAY;
        if (recompute) o.geometry.computeVertexNormals();
      }
    });
    const box = new Box3().setFromObject(scene);
    const c = new Vector3();
    box.getCenter(c);
    const size = new Vector3();
    box.getSize(size);
    return { center: c, radius: Math.max(size.x, size.y, size.z) };
  }, [scene, flat, recompute, env, viewer]);

  useLayoutEffect(() => {
    const camPos = orbitPosition(center, radius * dist, az, el);
    camera.position.copy(camPos);
    camera.lookAt(center);
    camera.updateProjectionMatrix();
    // Key light rakes from the camera's azimuth at a low elevation, so the rim
    // facing the camera is always grazed — the worst case for facets, everywhere.
    if (keyRef.current) {
      keyRef.current.position.copy(orbitPosition(center, radius * 4, az, keyEl));
      keyRef.current.target.position.copy(center);
      keyRef.current.target.updateMatrixWorld();
    }
  }, [camera, center, radius, az, el, dist, keyEl]);

  if (viewer) {
    // Generic glTF-viewer rig: a headlight from the camera (the usual viewer default)
    // + sky/ground hemisphere + soft fill. Edge roundness reads via the highlight
    // roll-off here, so a flat edge shows as a hard highlight cutoff.
    return (
      <group>
        <primitive object={scene} />
        <directionalLight
          position={orbitPosition(center, radius * 4, az, el).toArray()}
          intensity={2.4}
        />
        <hemisphereLight args={["#dfeaf5", "#8595a3", 0.9]} />
        <directionalLight
          position={orbitPosition(center, radius * 4, az + 140, 40).toArray()}
          intensity={0.5}
        />
        <ambientLight intensity={0.25} />
      </group>
    );
  }
  if (env) {
    // Studio-IBL-like rig: even diffuse from a hemisphere + a ring of soft lights
    // from all azimuths (and one from high), so the glossy clay sweeps the
    // "environment" across the surface — the condition a glTF viewer renders in,
    // where normal banding shows even when the matte grazing key hides it.
    return (
      <group>
        <primitive object={scene} />
        <hemisphereLight args={["#ffffff", "#9aa6b2", 1.1]} />
        <ambientLight intensity={0.35} />
        {[0, 60, 120, 180, 240, 300].map((a) => (
          <directionalLight
            key={a}
            position={orbitPosition(center, radius * 4, a, 28).toArray()}
            intensity={0.7}
          />
        ))}
        <directionalLight
          position={orbitPosition(center, radius * 4, az, 70).toArray()}
          intensity={1.0}
        />
      </group>
    );
  }
  return (
    <group>
      <primitive object={scene} />
      <directionalLight ref={keyRef} intensity={3.2} />
      {/* soft fill from the opposite low side + low ambient so shadows aren't black */}
      <directionalLight
        position={orbitPosition(center, radius * 4, az + 150, 35).toArray()}
        intensity={0.45}
      />
      <ambientLight intensity={0.22} />
    </group>
  );
}

// Sets the harness-readiness flag a few frames after the clay mesh mounts, so the
// puppeteer driver captures a settled frame (same contract as /shot).
function Settle({ frames = 6 }: { frames?: number }) {
  const n = useRef(0);
  useFrame(() => {
    n.current += 1;
    if (n.current === frames && typeof window !== "undefined") window.__shotReady = true;
  });
  return null;
}

function GeomShot() {
  const search = Route.useSearch();
  // `?viewer=1` reproduces a generic glTF viewer (VS Code etc.): a blue vertical
  // gradient backdrop + a camera headlight, the exact condition the operator inspects
  // in. Edge roundness reads very differently here than under the matte grazing key.
  const viewer = typeof window !== "undefined" && window.location.search.includes("viewer=1");
  const bg = viewer ? "linear-gradient(180deg, #c2dbf0 0%, #6a93ba 55%, #3f6f99 100%)" : "#33414d";
  return (
    <div className="h-screen w-screen" style={{ background: bg }} data-testid="geom-shot">
      <Canvas
        frameloop="always"
        dpr={1}
        camera={{ fov: 32, near: 0.001, far: 100 }}
        gl={{ antialias: true, toneMapping: ACESFilmicToneMapping, toneMappingExposure: 1.0 }}
      >
        <Suspense fallback={null}>
          <ClayPillow {...search} />
        </Suspense>
        <Settle />
      </Canvas>
    </div>
  );
}
